import api from "../utils/axiosConfig";
import { dedupeInFlight } from "../utils/inFlight";

/**
 * The reveal ceremony: the popup that runs when a round stops taking bets.
 *
 * Three acts — the round counts down, its numbers open one position at a time,
 * then the player is told what their own slip did — and the server serves each
 * of them three ways: fetched here, pushed as a `round.reveal` / `round.outcome`
 * event, or answered on a `round-reveal` / `my-round-outcome` live subscription.
 * All three carry the same payload, which is why these types are shared by all
 * of them rather than one shape per transport.
 *
 * Unlike the rest of `src/api`, nothing here is remapped into the legacy
 * `snake_case` deck shapes. This endpoint was written for this feature, so the
 * server's payload *is* the view model and a translation layer would only be
 * somewhere for the two to drift apart.
 */

/** Where the round is. The popup is a switch on this and nothing else. */
export type RevealPhase =
  /** Still taking bets — the popup is not up. */
  | "betting"
  /** The book has shut; counting down to the numbers. */
  | "lockdown"
  /** Past the close, numbers not published yet. */
  | "drawing"
  /** The numbers are out — open them. */
  | "result"
  /** Judged and paid; the outcome card is final. */
  | "settled"
  /** Cancelled and refunded. Nothing to reveal — close the popup. */
  | "void";

/** Server-supplied pacing, so every client opens the numbers on the same beat. */
export interface RevealAnimation {
  numberIntervalMs: number;
  numbersDurationMs: number;
  outcomeHoldMs: number;
  totalDurationMs: number;
}

export interface RoundReveal {
  drawNo: number;
  periodCode: string;
  phase: RevealPhase;
  status: string;
  positionCount: number;
  numberRange: { min: number; max: number };
  opensAt: string;
  betsCloseAt: string;
  /** What the countdown targets. Never count down from a local clock. */
  closesAt: string;
  drawnAt: string | null;
  settledAt: string | null;
  lockdownSeconds: number;
  secondsUntilLockdown: number;
  secondsUntilReveal: number;
  isBettingOpen: boolean;
  /** Empty until `phase` reaches `result`, then one number per position. */
  results: number[];
  totals?: {
    betCount: number;
    stakeCents: number;
    payoutCents: number;
    netCents: number;
  };
  isManualResult: boolean;
  animation: RevealAnimation;
  /** Compare with the local clock to correct for skew before counting down. */
  serverTime: string;
}

export interface RevealBet {
  id: string;
  drawNo: number;
  position: number;
  betType: string;
  pickedNumber: number | null;
  stakeCents: number;
  oddsBasisPoints: number;
  status: string;
  payoutCents: number;
  resultNumber: number | null;
  potentialPayoutCents: number;
  /**
   * True once settled a winner, or once the published numbers say it will be.
   * `null` while the round has no numbers — which is not "did not win".
   */
  isWinner: boolean | null;
}

export interface RoundOutcomeSummary {
  betCount: number;
  pendingCount: number;
  wonCount: number;
  lostCount: number;
  refundedCount: number;
  stakeCents: number;
  payoutCents: number;
  potentialPayoutCents: number;
  /** Payout minus stake, from the player's side — positive is a win. */
  netCents: number;
}

export interface RoundOutcome {
  drawNo: number;
  periodCode: string;
  phase: RevealPhase;
  status: "pending" | "settled";
  /** `none` means the player sat the round out, which is not `loss`. */
  outcome: "none" | "pending" | "win" | "loss" | "refunded";
  results: number[];
  drawnAt: string | null;
  settledAt: string | null;
  hasBets: boolean;
  summary: RoundOutcomeSummary;
  bets: RevealBet[];
  balanceCents: number | null;
  serverTime: string;
}

export const REVEAL_ENDPOINTS = {
  reveal: "/draws/reveal",
  outcome: "/me/rounds/outcome",
};

/**
 * The ceremony's clock and numbers.
 *
 * `drawNo` pins one round. Pass it once the popup is running: the next round
 * opens the instant this one settles, so an unpinned read mid-animation would
 * answer with a round the player has not seen the numbers for.
 */
export const getRoundReveal = async (drawNo?: number): Promise<RoundReveal> => {
  const params = drawNo ? { drawNo } : undefined;
  const response = await dedupeInFlight(
    `GET /draws/reveal${drawNo ? `?drawNo=${drawNo}` : ""}`,
    () => api.get<RoundReveal>(REVEAL_ENDPOINTS.reveal, { params })
  );
  return response.data;
};

/** This player's own card for the round. Requires a token. */
export const getRoundOutcome = async (drawNo?: number): Promise<RoundOutcome> => {
  const params = drawNo ? { drawNo } : undefined;
  const response = await api.get<RoundOutcome>(REVEAL_ENDPOINTS.outcome, { params });
  return response.data;
};
